
import Vue from 'vue';
import type { PropType } from 'vue';
import dayjs from 'dayjs';
import { IDateTimeSearch } from '@/types/misc_data';
import MISC_DATA from '@/store/modules/MiscDataModule';
import BOOKING from '@/store/modules/BookingModule';

export default Vue.extend({
	name: 'DateTimePicker',

	mixins: [],

	props: {
		min: {
			type: String,
			default: () => dayjs().format() // now
		},
		max: {
			type: String,
			default: undefined
		},
		parentDates: {
			type: Object as PropType<IDateTimeSearch>,
			default: undefined
		},
		dateFromExtraZone: {
			type: String,
			default: ''
		},
		disableRange: Boolean,
		onlyStartTime: Boolean,
		onlyEndTime: Boolean
	},

	data() {
		return {
			dayjs: dayjs,
			picker_date: null as string | null,
			picker_range: null as string[] | null,

			start_time: null as string | null,
			end_time: null as string | null,

			time_slots_start: ['00:00', '06:00', '12:00', '18:00'],
			time_slots_end: ['05:59', '11:59', '17:59', '23:59'],

			// Store opening hours data
			nonWorkingDays: [] as string[],
			partiallyClosedDays: [] as string[],
			nonWorkingSlots: {} as { [key: string]: number[] }
		};
	},

	computed: {
		MISC_DATA: () => MISC_DATA,
		BOOKING: () => BOOKING,

		dateRange(): string[] | null {
			// Sorted date range in case user chooses start date after end date
			if (this.picker_range && this.picker_range.length > 1) {
				return this.picker_range.slice().sort((a, b) => {
					return a > b ? 1 : a < b ? -1 : 0;
				});
			}
			return this.picker_range;
		},

		localeToBCP47(): string {
			return this.$i18n.locale.split('-')[0];
		},

		sameDay(): boolean {
			if (!this.dateRange) {
				return false;
			}
			return this.dateRange.every((slot) => slot === (this.dateRange as string[])[0]);
		},

		showTimeSlots(): boolean {
			return this.disableRange ? this.picker_date !== null : this.picker_range !== null;
		},

		minTimeProcessed(): string {
			if (this.disableRange && this.onlyEndTime) {
				// add 6 hours here since it's using the booking end time for min
				return dayjs(this.min).add(6, 'hours').format();
			}
			return this.min;
		},

		disableNext(): boolean {
			if (this.disableRange) {
				return this.onlyStartTime ? !this.picker_date || !this.start_time : !this.picker_date || !this.end_time;
			}
			return !this.picker_range || !this.start_time || !this.end_time;
		},

		startTimeDisabledList(): string[] | null {
			if ((this.disableRange && !this.picker_date) || (!this.disableRange && !this.dateRange)) {
				return null;
			}
			const black_list = [];
			// use min which is either a booking date_time or now
			const min_time = dayjs(this.minTimeProcessed).format('HH:mm');

			const date = this.dateRange ? this.dateRange[0] : this.picker_date;

			const slots = this.onlyEndTime ? this.time_slots_end : this.time_slots_start;

			// Check if start day chosen is min
			if (date === dayjs(this.min).format('YYYY-MM-DD')) {
				for (const t in slots) {
					if (slots[t] < min_time) {
						black_list.push(slots[t]);
					}
				}
				// Remove the last slot when using default min (now) because we can still choose current slot
				// $options.propsData contains props that WERE passed and not the default ones
				if (this.$options.propsData && !Object.keys(this.$options.propsData).includes('min')) {
					black_list.pop();
				}
			}
			// Check if there is a max and start date chosen is the same day
			if (this.max && date === dayjs(this.max).format('YYYY-MM-DD')) {
				// If disable range (upgrade) substract 6 hours so user can't choose the same time as booking start
				const max_time = this.disableRange ? dayjs(this.max).subtract(6, 'hours').format('HH:mm') : dayjs(this.max).format('HH:mm');
				for (const t in slots) {
					if (slots[t] > max_time) {
						black_list.push(slots[t]);
					}
				}
			}

			// Add slots that are outside opening hours
			if (date) {
				const dateStr = date.toString();
				if (this.nonWorkingSlots[dateStr]) {
					for (const t in slots) {
						const slotIndex = parseInt(t);
						if (this.nonWorkingSlots[dateStr].includes(slotIndex)) {
							black_list.push(slots[t]);
						}
					}
				}
			}

			return black_list;
		},

		endTimeDisabledList(): string[] | null {
			const black_list = [];
			// Get the start time if there is one (Priority to start_time)
			// const start_time_picked = this.start_time ? this.start_time : this.startTimeMin[0]

			if (this.start_time && this.sameDay) {
				const start_index = this.time_slots_start.indexOf(this.start_time);
				// look through time_slots_end and push times for which i is less than start index
				for (const i in this.time_slots_end) {
					if ((i as any) < start_index) {
						black_list.push(this.time_slots_end[i]);
					}
				}
			}
			// if max is set and max date was picked, add the timeslots outside of range to the blacklist
			if (this.max) {
				const max_date_picked = this.onlyEndTime
					? this.picker_date && this.picker_date === dayjs(this.max).format('YYYY-MM-DD')
					: this.dateRange && this.dateRange.includes(dayjs(this.max).format('YYYY-MM-DD'));
				if (max_date_picked) {
					const max_time = dayjs(this.max).format('HH:mm');
					for (const t in this.time_slots_end) {
						if (this.time_slots_end[t] > max_time) {
							black_list.push(this.time_slots_end[t]);
						}
					}
				}
			}

			// Add slots that are outside opening hours
			const date = this.dateRange ? (this.dateRange.length > 1 ? this.dateRange[1] : this.dateRange[0]) : this.picker_date;

			if (date) {
				const dateStr = date.toString();
				if (this.nonWorkingSlots[dateStr]) {
					for (const t in this.time_slots_end) {
						const slotIndex = parseInt(t);
						if (this.nonWorkingSlots[dateStr].includes(slotIndex)) {
							black_list.push(this.time_slots_end[t]);
						}
					}
				}
			}

			return black_list;
		},

		startDateTimeDisplay(): string | null {
			if (!this.dateRange || (this.disableRange && !this.picker_date)) {
				return null;
			}

			const date = this.disableRange ? this.picker_date : this.dateRange[0];

			if (!this.start_time) {
				return dayjs(date).format('dd, DD. MMMM');
			} else {
				return this.formatDateTime(`${date}T${this.start_time}`);
			}
		},

		endDateTimeDisplay(): string | null {
			if (!this.dateRange) {
				return null;
			}

			const end_date = this.dateRange.length > 1 ? this.dateRange[1] : this.dateRange[0];

			if (!this.end_time) {
				return dayjs(end_date).format('dd, DD. MMMM');
			} else {
				return this.formatDateTime(`${end_date}T${this.end_time}`);
			}
		}
	},

	watch: {
		startTimeDisabledList() {
			if (this.startTimeDisabledList && this.start_time) {
				for (const t in this.time_slots_start) {
					if (this.startTimeDisabledList.includes(this.time_slots_start[t]) && this.time_slots_start[t] === this.start_time) {
						this.start_time = this.time_slots_start[+t + 1];
					}
				}
			}
		},

		endTimeDisabledList() {
			// Update end_time if it was already set and now disabled
			if (this.endTimeDisabledList && this.end_time && this.endTimeDisabledList.includes(this.end_time)) {
				for (const t in this.time_slots_end) {
					if (!this.endTimeDisabledList.includes(this.time_slots_end[t])) {
						this.end_time = this.time_slots_end[t];
						return;
					}
				}
			}
		},

		parentDates() {
			if (!this.parentDates) {
				this.reset();
				return;
			}

			const dates = this.parentDates;

			if (this.disableRange) {
				if (this.onlyStartTime) {
					this.picker_date = dayjs(dates.start).format('YYYY-MM-DD');
					this.start_time = dayjs(dates.start).format('HH:mm');
				} else {
					this.picker_date = dayjs(dates.end).format('YYYY-MM-DD');
					this.end_time = dayjs(dates.end).format('HH:mm');
				}
			} else {
				this.picker_range = [dayjs(dates.start).format('YYYY-MM-DD'), dayjs(dates.end).format('YYYY-MM-DD')];
				this.start_time = dayjs(dates.start).format('HH:mm');
				this.end_time = dayjs(dates.end).format('HH:mm');
			}
		},

		picker_date: {
			handler(newVal) {
				if (newVal) {
					// When a date is picked, calculate disabled time slots
					this.loadOpeningHoursForDate(newVal);
				}
			},
			immediate: true
		},

		picker_range: {
			handler(newVal) {
				if (newVal && newVal.length > 0) {
					// When date range changes, calculate disabled time slots for each date
					for (const date of newVal) {
						this.loadOpeningHoursForDate(date);
					}
				}
			},
			immediate: true
		}
	},

	async mounted() {
		// Load opening hours when component is mounted
		await this.loadOpeningHours();
	},

	methods: {
		chooseDates() {
			if (this.disableRange && !this.disableNext) {
				this.$emit('picker-date', `${this.picker_date}T${this.onlyStartTime ? this.start_time : this.end_time}`);
				this.close();
				return;
			}

			if (this.disableNext || !this.dateRange) {
				return;
			}
			const same_day = this.dateRange && this.dateRange.length < 2;

			const start = `${this.dateRange[0]}T${this.start_time}`;
			const end = `${this.dateRange[same_day ? 0 : 1]}T${this.end_time}`;

			this.$emit('picker-payload', { start: start, end: end });
			this.close();
		},

		formatDateTime(date: string): string {
			if (this.$isDomain('hu')) {
				return dayjs(date).format('dd, MMMM DD, HH:mm');
			}

			return dayjs(date).format('dd, DD. MMMM, HH:mm');
		},

		reset(): void {
			this.picker_range = null;
			this.picker_date = null;
			this.start_time = null;
			this.end_time = null;
		},

		close(): void {
			this.reset();
			this.$emit('close');
		},

		getEventColor(date: string): string {
			// If the date is in nonWorkingDays (fully closed), return red
			// If the date is in partiallyClosedDays (some time slots closed), return orange
			if (this.nonWorkingDays.includes(date)) {
				return 'grey';
			} else if (this.partiallyClosedDays.includes(date)) {
				return 'orange';
			}
			return 'green';
		},

		allowedDates(date: string): boolean {
			// Disable dates that are fully closed (all slots unavailable)
			return !this.nonWorkingDays.includes(date);
		},

		isStartTimeDisabled(time: string): boolean {
			if (!this.startTimeDisabledList) {
				return false;
			}
			return this.startTimeDisabledList.includes(time);
		},

		isEndTimeDisabled(time: string): boolean {
			if (!this.endTimeDisabledList) {
				return false;
			}
			return this.endTimeDisabledList.includes(time);
		},

		async loadOpeningHours() {
			// Get the truck data from the booking
			const booking = this.BOOKING.data;
			if (!booking || !booking.truck) {
				console.log('DateTimePicker: No booking data or truck information available');
				return;
			}

			// Load truck opening hours
			let openingHours;
			if (typeof booking.truck === 'string') {
				// If truck is just an ID, we need to fetch the truck data
				console.log('DateTimePicker: Truck is just an ID, fetching truck details');
				try {
					// Import TRUCK module for API access
					const TRUCK = await import('@/store/modules/TruckModule').then((m) => m.default);

					// Fetch truck details
					const truckDetails = await TRUCK.GET_TRUCK_DATA({ truck_id: booking.truck });

					// Import Truck type to check instance
					const { Truck } = await import('@/types/truck');

					// Check if response is a valid Truck object and not an ErrorResponse
					if (truckDetails instanceof Truck && truckDetails.site && typeof truckDetails.site !== 'string' && truckDetails.site.opening_hours) {
						openingHours = truckDetails.site.opening_hours;
						console.log('DateTimePicker: Successfully fetched opening hours data', openingHours);
					} else {
						console.log('DateTimePicker: Truck fetched but no opening hours found', truckDetails);
					}
				} catch (error) {
					console.error('DateTimePicker: Error fetching truck details', error);
				}
			} else if (booking.truck.site && booking.truck.site.opening_hours) {
				// If truck is an object, extract opening hours
				openingHours = booking.truck.site.opening_hours;
				console.log('DateTimePicker: Found opening hours data', openingHours);
			} else {
				console.log('DateTimePicker: Truck object exists but no opening hours data found');
			}

			if (!openingHours) {
				return;
			}

			// Process opening hours for the next 8 weeks
			const startDate = dayjs().startOf('day');
			const endDate = dayjs().add(8, 'weeks').endOf('day');
			const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

			let currentDate = startDate;
			while (currentDate.isBefore(endDate)) {
				const dateStr = currentDate.format('YYYY-MM-DD');
				const dayOfWeek = currentDate.day(); // 0 = Sunday, 1 = Monday, etc.
				const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to 0 = Monday format
				const dayName = weekDays[dayIndex];

				// Check if the day is open
				const dayHours = openingHours[dayName];
				if (!dayHours || !dayHours.open) {
					// If the day is fully closed
					this.nonWorkingDays.push(dateStr);
				} else {
					// Check if there are partial restrictions
					const isFullyOpen = this.isFullyOpen(dayHours);
					if (!isFullyOpen) {
						this.partiallyClosedDays.push(dateStr);

						// Calculate which slots are unavailable
						const disabledSlots = this.getDisabledSlotsForDay(dayHours);
						if (disabledSlots.length > 0) {
							this.nonWorkingSlots[dateStr] = disabledSlots;
						}
					}
				}

				// Move to the next day
				currentDate = currentDate.add(1, 'day');
			}
		},

		isFullyOpen(dayHours: any): boolean {
			// Check if the location is open for the full day
			return dayHours.start === '00:00' && dayHours.end === '24:00';
		},

		getDisabledSlotsForDay(dayHours: any): number[] {
			const disabledSlots = [];
			const slots = [
				{ start: '00:00', end: '05:59', index: 0 },
				{ start: '06:00', end: '11:59', index: 1 },
				{ start: '12:00', end: '17:59', index: 2 },
				{ start: '18:00', end: '23:59', index: 3 }
			];

			// Convert opening times to minutes for easier comparison
			const openingMinutes = this.timeToMinutes(dayHours.start);
			const closingMinutes = this.timeToMinutes(dayHours.end);

			for (const slot of slots) {
				// Convert slot times to minutes
				const slotStartMinutes = this.timeToMinutes(slot.start);
				const slotEndMinutes = this.timeToMinutes(slot.end);

				// A slot is disabled if:
				// 1. The slot starts before opening time AND ends before opening time
				// 2. The slot starts after closing time
				// 3. The slot ends after closing time
				const isDisabled = (slotStartMinutes < openingMinutes && slotEndMinutes < openingMinutes) || slotStartMinutes >= closingMinutes || slotEndMinutes > closingMinutes;

				if (isDisabled) {
					disabledSlots.push(slot.index);
				}
			}

			return disabledSlots;
		},

		timeToMinutes(time: string): number {
			const [hours, minutes] = time.split(':').map(Number);
			return hours * 60 + minutes;
		},

		loadOpeningHoursForDate(_date: string) {
			// This function can be used to load additional opening hours data
			// for a specific date if needed
		}
	}
});
